import { Button, Icon, Input, Typography } from '@smartgift/frontend-toolkit';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatCurrency } from '@hero/utils';

import * as S from './style';

const CustomAmountInput = ({
  customAmount,
  setCustomAmount,
  isEditingCustomAmount,
  setIsEditingCustomAmount,
  minAmount,
  maxAmount,
  customAmountSku,
  setSelectedSkus
}) => {
  const { t } = useTranslation();

  const [value, setValue] = useState(customAmount);
  const [isEntered, setIsEntered] = useState(false);

  useEffect(() => {
    setValue(customAmount);
    if (customAmount) {
      setIsEntered(true);
    }
  }, [customAmount]);

  const handleClose = () => {
    setValue(customAmount);
    setIsEditingCustomAmount(false);
  };

  const handleSave = () => {
    if (hasError) return;
    setCustomAmount(value);
    setIsEditingCustomAmount(false);
    setIsEntered(true);
    setSelectedSkus([
      {
        ...customAmountSku,
        priceBreakdown: {
          ...customAmountSku.priceBreakdown,
          display: Number(value)
        },
        price: Number(value)
      }
    ]);
  };

  const hasError = useMemo(() => {
    if (!value) return null;

    const numericValue = Number(value);
    if (isNaN(numericValue) || !Number.isInteger(numericValue)) return t('mustBeWholeNumber');
    if (numericValue < minAmount || numericValue > maxAmount)
      return t('enterWholeNumberBetween', {
        minAmount: formatCurrency(minAmount),
        maxAmount: formatCurrency(maxAmount)
      });
    return null;
  }, [value, minAmount, maxAmount]);

  return (
    <S.CustomAmountWrapper>
      {(!isEntered || isEditingCustomAmount) && (
        <Button
          variant="link"
          size="sm"
          color="neutral"
          onClick={() => setIsEditingCustomAmount(true)}
        >
          {t('enterCustomAmount')}
        </Button>
      )}
      {isEditingCustomAmount && (
        <S.CustomAmountInputWrapper>
          <Input
            error={!!hasError}
            helperText={t('enterWholeNumberBetween', {
              minAmount: formatCurrency(minAmount),
              maxAmount: formatCurrency(maxAmount)
            })}
            value={value}
            onChange={e => setValue(e.target.value)}
            inputProps={{
              autoComplete: 'off',
              type: 'number'
            }}
            helperProps={{
              style: {
                position: 'absolute',
                bottom: -20,
                margin: 0,
                minWidth: 300
              }
            }}
          />
          <Typography variant="body2">$</Typography>
          <S.ActionButton onClick={handleClose}>
            <Icon name="close" />
          </S.ActionButton>
          <S.ActionButton disabled={!!hasError || !value} onClick={handleSave}>
            <Icon name="success" />
          </S.ActionButton>
        </S.CustomAmountInputWrapper>
      )}
    </S.CustomAmountWrapper>
  );
};

CustomAmountInput.propTypes = {
  customAmount: PropTypes.number,
  setCustomAmount: PropTypes.func.isRequired,
  isEditingCustomAmount: PropTypes.bool.isRequired,
  setIsEditingCustomAmount: PropTypes.func.isRequired,
  minAmount: PropTypes.number.isRequired,
  maxAmount: PropTypes.number.isRequired,
  customAmountSku: PropTypes.object.isRequired,
  setSelectedSkus: PropTypes.func.isRequired
};

export { CustomAmountInput };
